import _cloneDeep from "lodash.clonedeep";
import { useMutation, useQueryClient } from "react-query";
import useAnalytics from "./useAnalytics";
import useApiFetch from "./useApiFetch";
import useClientContext, { setCurrentEntry, setModal } from "./useClientContext";
import useGame from "./useGame";
import usePicksContext, { PicksState, updateNeedsToSave } from "./usePicksContext";
import { ISession, PlayoffsEntry } from "./useSession";

/**
 * Our api response: an array of picks for the active round.
 */
interface PicksResponse {
  picks: APIPick[];
  error?: number;
}

interface APIPick {
  bonus_points: string;
  driver_id: number;
  points: string;
  round_id: number;
  slot_seq: number;
  total_points: string;
}

export default function useSendPickMutation() {
  const [state, picksContextDispatch] = usePicksContext();
  const { fetchApiEndpoint } = useApiFetch();
  const [{ currentEntry, openModal }, clientContextDispatch] = useClientContext();
  const queryClient = useQueryClient();
  const { data: game } = useGame();
  const current_round = game.state?.current_round ?? 1;
  const { trackEvent } = useAnalytics();

  function submitPick() {
    const newPicks = formatPicksPayload(state.picks, currentEntry.picks);
    const body = JSON.stringify({ picks: newPicks[current_round] });
    trackEvent("picks:save_picks");

    // return Promise.reject("Boom 💥");

    return fetchApiEndpoint<PicksResponse>(`/api/v1/chase/entries/${currentEntry.id}/picks.json`, {
      method: "POST",
      body,
    });
  }

  function updateSessionFromPicksResponse(picksRes: PicksResponse, session: ISession): ISession {
    const newSession = _cloneDeep(session);
    const newPicks = picksRes.picks.map((p) => (p ? p.driver_id : p));

    if (!newPicks) {
      throw new Error(`API Error: could not update picks based on API response: ${newPicks}`);
    }

    if (!newSession.entries.chase) {
      newSession.entries.chase = [];
    }

    newSession.entries.chase = newSession.entries.chase.map((entry) => {
      if (entry.id === currentEntry.id) {
        const newEntry = _cloneDeep(entry);
        newEntry.picks[current_round] = newPicks;
        return newEntry;
      }
      return entry;
    });

    return newSession;
  }

  return useMutation(submitPick, {
    onMutate: () => {
      const prevSession: ISession = queryClient.getQueryData("session");

      const newEntry = _cloneDeep(currentEntry);
      newEntry.picks = formatPicksPayload(state.picks, currentEntry.picks);

      queryClient.setQueryData<ISession>("session", (session) =>
        updateSessionFromPicksResponse({ picks: newEntry.picks[current_round] }, session),
      );

      return { prevSession };
    },
    onSuccess: (data) => {
      picksContextDispatch(updateNeedsToSave(false));
      queryClient.setQueryData<ISession>("session", (prevSession) =>
        updateSessionFromPicksResponse(data, prevSession),
      );

      const newEntry = _cloneDeep(currentEntry);
      newEntry.picks = formatPicksPayload(state.picks, currentEntry.picks);
      clientContextDispatch(setCurrentEntry(newEntry));

      if (
        !openModal.options?.hasShownPicksComplete &&
        newEntry.picks[current_round].every((p) => Boolean(p))
      ) {
        clientContextDispatch(setModal("PICKS_COMPLETE", { hasShownPicksComplete: true }));
      }
    },
    onError: (error, vars, context) => {
      picksContextDispatch(updateNeedsToSave(true));
      queryClient.setQueryData("session", context.prevSession);
    },
  });
}

function formatPicksPayload(
  picks: PicksState["picks"],
  currentEntryPicks: PlayoffsEntry["picks"],
): PlayoffsEntry["picks"] {
  const newPicks = _cloneDeep(currentEntryPicks);

  for (const [key, value] of Object.entries(picks)) {
    const newValue = value.map((slot) => slot.pick);
    newPicks[key] = newValue;
  }
  return newPicks;
}
